import TradeTermsModal from '@components/TradeTermsModal';
import {
  DropdownFormField,
  DropdownWithTooltipFormField,
  MaskedCurrencyFormField,
  MaskedNumberFormField,
  MaskedPercentageFormField,
} from '@elements/FormField';
import { CommonUtility } from '@utility/common';
import { Info } from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import { type HTMLAttributes, useState } from 'react';
import { DiscountRules, DiscountRulesList } from './purchase-order';

type DivProps = HTMLAttributes<HTMLDivElement>;

/* The amount line sits under the input rather than beside it. */
const PERCENT_STACK = 'sk:flex-col sk:items-stretch';
type SpanProps = HTMLAttributes<HTMLSpanElement>;

/** A field cell whose amount line sits under its input. */
export function PercentageDivWrapper({ className, ...rest }: DivProps) {
  return <div {...rest} className={cn('sk:min-w-0', className)} />;
}

/** A row of payment fields; one column on a phone. */
export function TradeWrapper({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:grid sk:grid-cols-1 sk:items-start sk:gap-x-4',
        className,
      )}
    />
  );
}

export function SupplierTotalAmount({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:mt-1.25 sk:flex sk:justify-end sk:border-0 sk:border-t sk:border-solid sk:border-rule',
        className,
      )}
    />
  );
}

/** The unit at the right end of a percentage input. */
export function InputLabel({ className, ...rest }: SpanProps) {
  return (
    <span
      {...rest}
      className={cn(
        'sk:pointer-events-none sk:absolute sk:top-0 sk:right-3 sk:flex sk:h-7 sk:items-center sk:text-xs sk:text-fg-tertiary',
        className,
      )}
    />
  );
}

export function AmountField({ className, ...rest }: DivProps) {
  return (
    <div
      {...rest}
      className={cn(
        'sk:mt-1.25 sk:flex sk:min-h-7 sk:items-center sk:border-0 sk:border-t sk:border-solid sk:border-rule',
        className,
      )}
    />
  );
}

function TradeTermsLabel({
  children,
  onInfo,
}: {
  children: string;
  onInfo: () => void;
}) {
  return (
    <span className="sk:mb-1 sk:flex sk:items-center sk:gap-1 sk:text-xs sk:text-fg-tertiary">
      {children}
      <button
        type="button"
        aria-label="About Incoterms"
        onClick={onInfo}
        className="sk:inline-flex sk:cursor-pointer sk:border-0 sk:bg-transparent sk:p-0 sk:text-fg-tertiary"
      >
        <Info />
      </button>
    </span>
  );
}

export function PaymentTerm({
  control,
  errors,
  total,
  tradeTermLoading,
  tradeTerms,
  discountRule,
  selectedId,
  showDiscount = true,
  showPrice = true,
  showPayment = true,
  tradeTermName = 'price_term',
  downPaymentName = 'down_payment',
  shipmentPaymentName = 'upon_shipment',
  isUser = false,
}) {
  const [open, setOpen] = useState(false);

  const onDiscountRuleChange = (e, data) => {
    if (data.value === 'percentage' && control.getValues().discount > 100) {
      control.setValue('discount', 100);
    }
    control.setValue('discount_rule', data.value);
  };

  /* `total` is only passed along with `showPayment`. */
  const amountUnder = (part: 'down' | 'upon') => (
    <>
      <InputLabel>%</InputLabel>
      {showPayment && (
        <div className="sk:mt-2 sk:ml-1 sk:self-start">
          {selectedId ? CommonUtility.currencyFormat(total[part]) : '$'}
        </div>
      )}
    </>
  );

  return (
    <div className="sk:mt-1">
      <TradeTermsModal open={open} setOpen={setOpen} />
      <TradeWrapper className="sk:sm:grid-cols-16">
        <div className="sk:min-w-0 sk:sm:col-span-4">
          <DropdownWithTooltipFormField
            name={tradeTermName}
            disabled={isUser}
            options={tradeTerms}
            loading={tradeTermLoading}
            control={control}
            defaultValue=""
            error={errors?.[tradeTermName]}
            label={
              <TradeTermsLabel onInfo={() => setOpen(true)}>
                Trade Terms
              </TradeTermsLabel>
            }
            placeholder={selectedId ? 'Select' : ''}
            required
          />
        </div>
        <PercentageDivWrapper className="sk:sm:col-span-3">
          <MaskedPercentageFormField
            name={downPaymentName}
            disabled={isUser}
            error={errors?.[downPaymentName]}
            label="Down Payment"
            control={control}
            style={{ width: '100%' }}
            defaultValue=""
            inputContainerClass={PERCENT_STACK}
            placeholder=" "
            maskOptions={{ allowDecimal: true, decimalLimit: 1 }}
            inputRight={amountUnder('down')}
          />
        </PercentageDivWrapper>
        <PercentageDivWrapper className="sk:sm:col-span-4">
          <MaskedPercentageFormField
            name={shipmentPaymentName}
            disabled={isUser}
            error={errors?.[shipmentPaymentName]}
            label="Upon Shipment Payment"
            control={control}
            defaultValue=""
            placeholder=" "
            inputContainerClass={PERCENT_STACK}
            style={{ width: '100%' }}
            maskOptions={{ allowDecimal: true, decimalLimit: 1 }}
            inputRight={amountUnder('upon')}
          />
        </PercentageDivWrapper>
        {showDiscount && (
          <>
            {discountRule === DiscountRules.Fixed ? (
              <MaskedCurrencyFormField
                name="discount"
                disabled={isUser}
                error={errors.discount}
                label="Discount"
                control={control}
                // @ts-expect-error - TS2322: Type 'string' is not assignable to type 'null | undefined'.
                defaultValue=""
                fieldClass="sk:sm:col-span-2"
                placeholder="0"
              />
            ) : (
              <PercentageDivWrapper className="sk:sm:col-span-2">
                <MaskedNumberFormField
                  name="discount"
                  disabled={isUser}
                  error={errors.discount}
                  label="Discount"
                  control={control}
                  defaultValue=""
                  placeholder="0"
                  className={PERCENT_STACK}
                  fieldClass="sk:mb-0!"
                  maskOptions={{
                    allowDecimal: true,
                    decimalSymbol: '.',
                    decimalLimit: 2,
                    integerLimit: 3,
                  }}
                  maxValue={100}
                  inputRight={
                    <>
                      <InputLabel>%</InputLabel>
                      {!selectedId && (
                        <div className="sk:mt-2 sk:self-start">-$</div>
                      )}
                      {total.discount > 0 && (
                        <div className="sk:my-2 sk:self-start">
                          -{CommonUtility.currencyFormat(total.discount)}
                        </div>
                      )}
                    </>
                  }
                />
              </PercentageDivWrapper>
            )}

            <DropdownFormField
              name="discount_rule"
              disabled={isUser}
              options={DiscountRulesList}
              control={control}
              placeholder=" "
              error={errors.discount_rule}
              fieldClass="sk:sm:col-span-3"
              label="Discount Rule"
              // @ts-expect-error - TS2322: Type '{ name: string; disabled: boolean; options: ({ text: string; value: "perce...
              onChange={onDiscountRuleChange}
            />
          </>
        )}
      </TradeWrapper>

      {showPrice && (
        <TradeWrapper className="sk:sm:grid-cols-16">
          <div className="sk:min-w-0 sk:sm:col-span-4">
            <MaskedNumberFormField
              label="Lead Time (Days)"
              placeholder="Days"
              name="lead_time"
              error={errors.lead_time}
              control={control}
              defaultValue=""
              maskOptions={{ integerLimit: 3 }}
              fieldClass="sk:mb-0!"
            />
          </div>
          <AmountField className="sk:sm:col-span-3">
            {!selectedId && <div>$</div>}
            {total.discount > 0 && (
              <div>{CommonUtility.currencyFormat(total.afterDiscountDown)}</div>
            )}
          </AmountField>
          <AmountField className="sk:sm:col-span-4">
            {!selectedId && <div>$</div>}
            {total.discount > 0 && (
              <div>{CommonUtility.currencyFormat(total.afterDiscountUpon)}</div>
            )}
          </AmountField>
          <AmountField className="sk:justify-end sk:sm:col-span-5">
            <span className="sk:mr-3 sk:text-base sk:text-fg-tertiary">
              Total
            </span>
            {selectedId && (
              <span className="sk:font-bold">
                {CommonUtility.currencyFormat(
                  total.discount ? total.total : total.grossTotal,
                )}
              </span>
            )}
          </AmountField>
        </TradeWrapper>
      )}
    </div>
  );
}

export function POPaymentTerm({
  control,
  errors,
  tradeTermLoading,
  tradeTerms,
  discountRule,
  setValue,
  getValues,
  defaultValue,
}) {
  const [open, setOpen] = useState(false);

  const onDiscountRuleChange = (e, data) => {
    if (data.value === 'percentage' && getValues().discount > 100) {
      setValue('discount', 100);
    }
    setValue('discount_rule', data.value);
  };

  return (
    <>
      <TradeTermsModal open={open} setOpen={setOpen} />
      <TradeWrapper className="sk:sm:grid-cols-2">
        <DropdownWithTooltipFormField
          name="price_term"
          options={tradeTerms}
          loading={tradeTermLoading}
          control={control}
          defaultValue={defaultValue || ''}
          error={errors?.price_term}
          label={
            <TradeTermsLabel onInfo={() => setOpen(true)}>
              Payment Terms
            </TradeTermsLabel>
          }
          placeholder="Select"
          required
        />
        <MaskedNumberFormField
          label="Lead Time (Days)"
          placeholder="Days"
          name="lead_time"
          error={errors.lead_time}
          control={control}
          defaultValue=""
          maskOptions={{ integerLimit: 3 }}
          fieldClass="sk:mb-0!"
        />
      </TradeWrapper>
      <TradeWrapper className="sk:sm:grid-cols-[5fr_5fr_3fr_4fr]">
        <MaskedPercentageFormField
          name="down_payment"
          error={errors?.down_payment}
          label="Down Payment"
          required
          control={control}
          defaultValue=""
          placeholder=" "
          maskOptions={{ allowDecimal: true, decimalLimit: 1 }}
          inputRight={<InputLabel>%</InputLabel>}
        />
        <MaskedPercentageFormField
          name="upon_shipment"
          error={errors?.upon_shipment}
          label="Upon Shipment Payment"
          required
          control={control}
          disabled
          defaultValue=""
          placeholder=" "
          maskOptions={{ allowDecimal: true, decimalLimit: 1 }}
          inputRight={<InputLabel>%</InputLabel>}
        />
        {discountRule === DiscountRules.Fixed ? (
          <MaskedCurrencyFormField
            name="discount"
            error={errors.discount}
            label="Discount"
            control={control}
            // @ts-expect-error - TS2322: Type 'string' is not assignable to type 'null | undefined'.
            defaultValue=""
            placeholder="0"
          />
        ) : (
          <MaskedNumberFormField
            name="discount"
            error={errors.discount}
            label="Discount"
            control={control}
            defaultValue=""
            placeholder="0"
            maskOptions={{
              allowDecimal: true,
              decimalSymbol: '.',
              decimalLimit: 2,
              integerLimit: 3,
            }}
            maxValue={100}
            inputRight={<InputLabel>%</InputLabel>}
          />
        )}

        <DropdownFormField
          name="discount_rule"
          options={DiscountRulesList}
          control={control}
          placeholder=" "
          error={errors.discount_rule}
          label="Discount Rule"
          // @ts-expect-error - TS2322: Type '{ name: string; options: ({ text: string; value: "percentage"; } | { text:...
          onChange={onDiscountRuleChange}
        />
      </TradeWrapper>
    </>
  );
}
